import * as XLSX from 'xlsx'
import { CONSTRUCT_ARRAY } from '../../utils/constructs'
import { formatDate } from '../../utils/formatDate'

/**
 * called by ExportData.js
 * creates XL file and triggers download of Answer-Sessions or Benchmarks
 * @param items <array> of either Answer-Sessions or Benchmarks
 * @param type <string> either 'answer-sessions' or 'benchmarks'
 */
export const createXLFile = (items, type) => {

  const dateToday = formatDate(new Date(), 'YYYY.MM.DD')
  const xLFileName = `${dateToday}_${type}.xlsx`

  const workbook = type === 'answer-sessions' ? createAnswerSessionBook(items) : createBenchmarkBook(items)

  // Create the Excel file and trigger download
  XLSX.writeFile(workbook, xLFileName)
}

/**
 * Creates workbook for XL-file of Answer-Sessions
 * @param sessions
 * @return {WorkBook}
 */
const createAnswerSessionBook = sessions => {

  const workbook = XLSX.utils.book_new()

  sessions.forEach((session, index) => {
    const { orgCategory, organizationID, module, userID, answers } = session

    // Create metadata rows
    const metaRows = [
      ['Modul:', module || ''],
      ['NPO Kategorie:', orgCategory || ''],
      ['NPO ID:', organizationID || ''],
      ['User ID:', userID || ''],
      [],
      ['Variabel', 'Antwort'], // column headers
    ]

    // Convert key-value answers into table rows
    const answerRows = Object.entries(answers || {}).map(([key, value]) => {
      if (typeof value === 'string' || typeof value === 'number') {
        return [key, value]
      }
      if (value === null || value === undefined) {
        return [key, String(value)] // 'null' or 'undefined'
      }
      if (Array.isArray(value)) {
        return [key, ...value]
      }
      if (typeof value === 'object') {  // this should be the only option left
        const flattenedEntries = Object.entries(value).flatMap(([k, v]) => [k, v])
        return [key, ...flattenedEntries] // main key, then subkey1, value1, subkey2, value2...
      }
      return []
    })

    const sheetData = [...metaRows, ...answerRows]
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData)
    const sheetName = `Session ${index + 1}`

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
  })

  return workbook
}

/**
 * Creates workbook for XL-file of Benchmarks
 * @param benchmarks
 * @return {WorkBook}
 */
const createBenchmarkBook = benchmarks => {
  const workbook = XLSX.utils.book_new()

  //  get the CONSTRUCT_ARRAY and reduce it to an object of modules, each an array of 3-letter construct abbreviations
  const constructArray = Object.fromEntries(
    Object.entries(CONSTRUCT_ARRAY).map(([key, value]) => [
      key,
      value.map(construct => construct.short),
    ]),
  )
  // console.log('constructArray:', constructArray)

  // sort the array of benchmark objects by module and then by category
  benchmarks.sort((a, b) => {
    // First sort by module (numerically, but they are strings so we convert to numbers)
    const moduleDiff = Number(a.module) - Number(b.module)
    if (moduleDiff !== 0) return moduleDiff
    // If modules are the same, sort by orgCategory (alphabetically)
    return a.orgCategory.localeCompare(b.orgCategory)
  })
  // console.log('benchmarks:', benchmarks)

  /* loop through each benchmark object:
    - create the header rows depending on the applicable array of construct abbreviations (separate function for this)
    - create the row array and populate the cells of module and category
    - create loop of benchmark constructs and push the mean and standard deviation values onto row array
    - create worksheet after finishing each module */
  const numCategories = benchmarks.length / Number(benchmarks[benchmarks.length - 1].module)
  let categoryCount = 0
  let metaRows = []
  let dataRows = []
  benchmarks.forEach(benchmarkObj => {
    const { orgCategory, module, benchmark } = benchmarkObj
    categoryCount++
    // only add new sheet if we're on the next module
    if (categoryCount === 1) {
      const currentConstructHeaders = []
      constructArray[`M${Number(module)}`].forEach(construct => {
        currentConstructHeaders.push(`${construct}\nMittelwert`, `${construct}\nStandardabweichung`)
      })
      metaRows = [
        // eslint-disable-next-line
        [, , , 'Konstrukte:'], // eslint doesn't like the commas in the middle of the array
        ['Modul', 'NPO\nKategorie', 'Anzahl\nNPOs', ...currentConstructHeaders],
      ]
      // console.log('metaRows:', metaRows)
    }

    const currentBenchmarkData = [module, orgCategory, benchmark.sample_size]
    constructArray[`M${Number(module)}`].forEach(construct => {
      currentBenchmarkData.push(benchmark[construct].mean ?? 'null', benchmark[construct].stdDev ?? 'null')
    })
    dataRows.push(currentBenchmarkData)

    // if we've come to the last category, save the sheet
    if (categoryCount === numCategories) {
      const sheetData = [...metaRows, ...dataRows]
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData)
      const sheetName = `Modul ${module}`
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

      metaRows = []
      dataRows = []
      categoryCount = 0
    }

  })

  return workbook
}

/* Old version until 11.8.25
export const createXLFile = (items, type) => {
  const workbook = XLSX.utils.book_new()
  const dateToday = formatDate(new Date(), 'YYYY.MM.DD')
  const xLFileName = `${dateToday}_${type}.xlsx`

  items.forEach((item, index) => {
    const { orgCategory, organizationID, module, userID, answers, benchmark } = item

    // Create metadata rows
    const metaRows = [
      ['Modul:', module || ''],
      ['NPO Kategorie:', orgCategory || ''],
    ]
    if (type === 'answer-sessions') {
      metaRows.push(
        ['NPO ID:', organizationID || ''],
        ['User ID:', userID || ''],
        [],
        ['Variabel', 'Antwort'], // column headers
      )
    }
    if (type === 'benchmarks') {
      metaRows.push(
        [],
        ['Konstrukt', 'Mittelwert', 'Standardabweichung'],
      )
    }

    const answerObj = type === 'answer-sessions' ? answers : benchmark
    // Convert key-value answers into table rows
    const answerRows = Object.entries(answerObj || {}).map(([key, value]) => {
      if (type === 'benchmarks' && typeof value === 'object') {
        return [key, value.mean, value.stdDev]
      }
      if (typeof value === 'string' || typeof value === 'number') {
        return [key, value]
      }
      if (value === null || value === undefined) {
        return [key, String(value)] // 'null' or 'undefined'
      }
      if (Array.isArray(value)) {
        return [key, ...value]
      }
      if (typeof value === 'object') {  // this should be the only option left and the only one in the benchmarks
        const flattenedEntries = Object.entries(value).flatMap(([k, v]) => [k, v])
        return [key, ...flattenedEntries] // main key, then subkey1, value1, subkey2, value2...
      }
      return []
    })

    const sheetData = [...metaRows, ...answerRows]
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData)
    const sheetName = type === 'answer-sessions' ? `Session ${index + 1}` : `M${module}-${orgCategory}`

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
  })

  // Create the Excel file and trigger download
  XLSX.writeFile(workbook, xLFileName)
}
 */
